import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
    Box,
    Button,
    Flex,
    Grid,
    Image,
    LoadingOverlay,
    NumberInput,
    Stack,
    Text,
    TextInput,
    Tooltip,
} from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { IconInfoCircle } from "@tabler/icons-react";
import dayjs from "dayjs";
import { pick } from "lodash";
import { useUploadImage } from "src/hooks/files/useUploadImage";

import { campaignImageCrop } from "../../hooks/campaigns";
import { SelectImageModal } from "../../modals/SelectImage";
import { FormItem } from "../FormItem";
import { RichTextEditor } from "../RichTextEditor";
import { SelectRegion } from "../SelectRegion";
import { useCreateCampaignInquiryFormContext } from "./formContext";

export interface CreateCampaignInquiryGeneralFormProps {}
export const CreateCampaignInquiryGeneralForm = ({}: CreateCampaignInquiryGeneralFormProps) => {
    const { t } = useTranslation(["common"]);
    const createCampaignInquiryForm = useCreateCampaignInquiryFormContext();

    const handleTenantChange = useCallback(async (tenantId: string) => {
        createCampaignInquiryForm.setFieldValue("tenantId", tenantId);
        createCampaignInquiryForm.setFieldValue("mainTenantId", tenantId);
        createCampaignInquiryForm.setFieldValue("displayTenantIds", [tenantId]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const { uploadImage, isUploadingImage } = useUploadImage();
    const handleImageUpload = useCallback(async (file: File) => {
        const image = await uploadImage(file);

        createCampaignInquiryForm.setFieldValue("images", [
            ...(createCampaignInquiryForm.values?.images || []),
            { file: image },
        ]);

        return image;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <>
            <LoadingOverlay visible={isUploadingImage} />
            <Grid>
                <Grid.Col xs={12} md={6}>
                    <TextInput
                        {...createCampaignInquiryForm.getInputProps("name")}
                        type="text"
                        label={t("campaign_name")}
                        placeholder={t("campaign_name")}
                        withAsterisk
                    />
                </Grid.Col>
                <Grid.Col xs={12} md={6}>
                    <SelectRegion
                        {...createCampaignInquiryForm.getInputProps("tenantId")}
                        label={t("country")}
                        placeholder={t("country")}
                        mb="xs"
                        onChange={handleTenantChange}
                        withAsterisk
                    />
                </Grid.Col>
            </Grid>
            <Grid>
                <Grid.Col xs={12} md={6}>
                    <NumberInput
                        {...createCampaignInquiryForm.getInputProps("settings.capacity")}
                        mb="xs"
                        type="number"
                        label={t("campaign_capacity")}
                        placeholder={t("campaign_capacity")}
                        min={0}
                        step={1}
                    />
                </Grid.Col>
                <Grid.Col xs={12} md={6}>
                    <DatePickerInput
                        {...createCampaignInquiryForm.getInputProps("settings.openAt")}
                        mb="md"
                        label={t("campaign_timeline_openat")}
                        value={
                            createCampaignInquiryForm?.values?.settings?.openAt
                                ? dayjs(createCampaignInquiryForm.values.settings.openAt).toDate()
                                : undefined
                        }
                    />
                </Grid.Col>
            </Grid>
            <Grid>
                <Grid.Col xs={12} sm={12} md={12}>
                    <FormItem
                        label={
                            <Tooltip w="250px" label={t("campaign_description_info")} withArrow multiline>
                                <Text w="fit-content">
                                    {t("campaign_description")} <IconInfoCircle size="12" />
                                </Text>
                            </Tooltip>
                        }
                    >
                        <RichTextEditor
                            {...createCampaignInquiryForm.getInputProps("description")}
                            onImageUpload={handleImageUpload}
                        />
                    </FormItem>
                </Grid.Col>
                <Grid.Col xs={12} sm={12} md={12}>
                    <FormItem label={t("campaign_thumbnailimage")}>
                        <Flex>
                            <Stack spacing="sm">
                                <Button
                                    variant="light"
                                    onClick={() =>
                                        SelectImageModal.display({
                                            title: t("thumbnailimage_upload"),
                                            openFileInputDialogOnMount: true,
                                            crop: campaignImageCrop,
                                            onSelect: (thumbnailImage) => {
                                                createCampaignInquiryForm.setFieldValue(
                                                    "thumbnailImage",
                                                    pick(thumbnailImage, ["id", "objectKey", "src"])
                                                );
                                            },
                                        })
                                    }
                                >
                                    {t("thumbnailimage_upload")}
                                </Button>
                                {!!createCampaignInquiryForm.values?.thumbnailImage?.src && (
                                    <Button
                                        variant="subtle"
                                        color="red"
                                        onClick={() => createCampaignInquiryForm.setFieldValue("thumbnailImage", null)}
                                    >
                                        {t("image_remove")}
                                    </Button>
                                )}
                            </Stack>
                            <Box ml="md" style={{ flex: 1 }}>
                                {!!createCampaignInquiryForm.values?.thumbnailImage?.src && (
                                    <Image
                                        radius="md"
                                        src={`${createCampaignInquiryForm.values.thumbnailImage.src}?w=320`}
                                        width="320px"
                                    />
                                )}
                            </Box>
                        </Flex>
                    </FormItem>
                </Grid.Col>
            </Grid>
        </>
    );
};
